import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";
import { CaseStrip } from "@/components/CasePreview";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Freelancer: Shop, Anpassungen, Conversion";
const DESCRIPTION =
  "Shopify Freelancer aus Deutschland: Shop aufbauen, Theme anpassen und Kaufabschlussrate verbessern. 110 € netto pro Stunde, Conversion-Projekte ab 3.990 € netto.";

const faqs = [
  {
    q: "Was macht ein Shopify Freelancer?",
    a: "Er baut Shopify-Shops auf, passt bestehende Shops an und verbessert die Kaufabschlussrate. Bei mir bekommen Sie alle drei Bereiche aus einer Hand und sprechen dabei mit der Person, die auch umsetzt. Wichtig: Ich bin unabhängiger Freelancer, nicht der offizielle Shopify-Support.",
  },
  {
    q: "Was kostet ein Shopify Freelancer?",
    a: "Anpassungen und Entwicklung rechne ich mit 110 € netto pro Stunde ab, den Aufwand nenne ich vorher. Ein kleiner neuer Shop beginnt bei rund 3.000 € als Festpreis. Conversion-Projekte starten bei 3.990 € netto. Zum Vergleich: Agenturen liegen häufig bei 8.000 bis 25.000 € und mehr für den Shopaufbau.",
  },
  {
    q: "Verkauft ein neuer Shop dann automatisch?",
    a: "Ehrlich gesagt: nein. Der schönste Laden bringt nichts, wenn keiner reinkommt. Besucher kommen über Google, Anzeigen und Empfehlungen. Ich baue Ihren Shop conversion-optimiert und berate Sie offen dazu, welches Budget Sie für Besucher einplanen sollten.",
  },
  {
    q: "Freelancer oder Agentur für Shopify?",
    a: "Bei einem Freelancer sprechen Sie direkt mit der Person, die umsetzt. Kürzere Wege, weniger Abstimmung, kein Retainer. Bei sehr großen Vorhaben mit vielen Beteiligten kann ein Team die bessere Wahl sein, das sage ich Ihnen dann auch.",
  },
  {
    q: "Arbeiten Sie mit bestehenden Shops?",
    a: "Ja, das ist der Normalfall. Der vorhandene Shop ist meist die bessere Ausgangslage als ein Neuaufbau, weil dort schon Daten darüber vorliegen, wo Kunden abspringen.",
  },
  {
    q: "Wie fange ich an?",
    a: "Sie schicken mir eine kurze Nachricht. Innerhalb von 48 Stunden bekommen Sie eine Einschätzung zu Ihrem Vorhaben, den empfohlenen nächsten Schritt und, soweit Ihre Angaben es hergeben, einen groben Kostenrahmen.",
  },
];

export const Route = createFileRoute("/shopify-freelancer/")({
  component: ShopifyFreelancerPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/shopify-freelancer` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/shopify-freelancer` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Shopify Freelancer, Nicolai Schnepf",
          url: `${SITE}/shopify-freelancer`,
          description: DESCRIPTION,
          areaServed: ["DE", "AT", "CH"],
          founder: { "@type": "Person", name: "Nicolai Schnepf" },
          knowsAbout: [
            "Shopify Shop Erstellung",
            "Shopify Theme Anpassung",
            "Shopify Liquid Entwicklung",
            "Shopify Conversion Optimierung",
          ],
          makesOffer: [
            {
              "@type": "Offer",
              name: "Shopify Shop erstellen lassen",
              url: `${SITE}/shopify-shop-erstellen-lassen`,
              priceCurrency: "EUR",
              description: "Festpreis nach Umfang.",
            },
            {
              "@type": "Offer",
              name: "Shopify Anpassungen und Entwicklung",
              url: `${SITE}/shopify-theme-anpassung`,
              price: "110",
              priceCurrency: "EUR",
              description: "110 € netto pro Stunde.",
            },
            {
              "@type": "Offer",
              name: "Shopify Conversion Optimierung",
              url: `${SITE}/shopify-conversion-optimierung`,
              price: "3990",
              priceCurrency: "EUR",
              description: "Festpreis ab 3.990 € netto.",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

const areas = [
  {
    title: "Shop aufbauen",
    text: "Neuer Shopify-Shop von der Struktur bis zum Start: Sortiment ordnen, Theme anpassen, Produkt- und Kategorieseiten aufbauen, Zahlungen und Versand einrichten, mobil prüfen, live gehen. Danach pflegen Sie den Shop selbst, mit einer kurzen Einweisung von mir.",
    price: "Kleiner Shop ab 3.000 €, Festpreis schriftlich",
    to: "/shopify-shop-erstellen-lassen" as const,
    cta: "Zur Shop-Erstellung",
  },
  {
    title: "Anpassen und entwickeln",
    text: "Sections, Produktseiten, Varianten, mobile Darstellung, Schnittstellen und technische Fehler im vorhandenen Theme. Gearbeitet wird in einer Kopie, veröffentlicht erst nach Ihrer Freigabe.",
    price: "110 € netto pro Stunde, Aufwand vor Start genannt",
    to: "/shopify-theme-anpassung" as const,
    cta: "Zur Theme-Anpassung",
  },
  {
    title: "Mehr Bestellungen holen",
    text: "Kaufabschlussrate verbessern: Audit von Produktseite, Warenkorb, mobilem Erlebnis und Ladezeit, dann Umsetzung im Theme mit klaren Messgrößen vorher und ehrlicher Auswertung danach.",
    price: "Festpreis ab 3.990 € netto",
    to: "/shopify-conversion-optimierung" as const,
    cta: "Zur Conversion-Optimierung",
  },
];

const reasons = [
  "Sie sprechen mit der Person, die Ihren Shop auch tatsächlich baut",
  "Umfang und Preis stehen fest, bevor gearbeitet wird",
  "Aufbau, Anpassung und Optimierung aus einer Hand",
  "Sauber im Theme statt einer Sammlung von Apps",
  "Ehrliche Ansage, wenn sich etwas für Sie nicht lohnt",
];

function ShopifyFreelancerPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="top">
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Nicolai Schnepf · Shopify Freelancer
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] md:text-6xl">
                Ihr Shopify-Shop soll mehr leisten.{" "}
                <span className="text-signal">Ich setze es um.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                Ich baue Shopify-Shops auf, passe bestehende Themes an und arbeite daran,
                dass aus Besuchern Käufer werden. Sie arbeiten direkt mit mir, nicht mit
                einer Zwischenebene.
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                Anpassungen und Entwicklung: 110 € netto pro Stunde. Conversion-Projekte ab
                3.990 € netto. Shopaufbau zum Festpreis nach Umfang.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
                >
                  Kundenprojekte ansehen
                </Link>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Unverbindlich · Antwort innerhalb von 48 Stunden
              </p>
            </div>
            <div id="anfrage">
              <LeadForm
                source="lp-freelancer"
                showServices
                headline="Shop kurz einschätzen lassen"
                messagePlaceholder="Was soll an Ihrem Shop besser werden?"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Drei Wege, wie wir zusammenarbeiten</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {areas.map((a) => (
                <div
                  key={a.title}
                  className="shadow-lift flex flex-col rounded-lg border border-border bg-card p-6"
                >
                  <h3 className="text-lg font-semibold">{a.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{a.text}</p>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                    {a.price}
                  </p>
                  <Link
                    to={a.to}
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary"
                  >
                    {a.cta} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Was Zusammenarbeit konkret kostet</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Keine versteckten Posten, keine offene Stundenliste. Das sind die
              Größenordnungen, mit denen Sie rechnen können.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { name: "Kleine Aufgabe", price: "ab 110 €", note: "Eine Stunde Entwicklung: ein Fehler, eine kleine Section, eine Frage, die schnell gelöst ist. Abgerechnet nach tatsächlichem Aufwand." },
                { name: "Neuer Shop", price: "ab 3.000 €", note: "Kleiner, sauber aufgebauter Shop zum Festpreis. Wachsende Shops mit mehr Produkten, Sprachen oder Schnittstellen liegen bei 4.000 bis 10.000 €. Agenturen: 8.000 bis 25.000 €+." },
                { name: "Conversion-Projekt", price: "ab 3.990 €", note: "Vier Wochen Sprint mit Audit, Umsetzung und Auswertung. Größere Programme über 3 oder 6 Monate: 9.900 € beziehungsweise 18.900 €." },
              ].map((p) => (
                <div key={p.name} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{p.name}</h3>
                  <p className="text-signal mt-2 text-2xl font-bold">{p.price}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.note}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Alle Angaben netto. Festpreise stehen schriftlich fest, bevor gearbeitet wird.
            </p>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Ehrliche Beratung
            </p>
            <h2 className="mt-4 text-3xl font-bold md:text-4xl">
              Der schönste Laden bringt nichts, <span className="text-signal">wenn keiner reinkommt.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Ein Onlineshop ist wie ein Geschäft in einer Seitenstraße: liebevoll eingerichtet,
              gute Produkte, faire Preise, aber niemand weiß, dass es existiert. Der Shop allein
              verkauft nichts. Ihr Shop muss verkaufen, und dafür braucht er Besucher.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Als seriöser Berater sage ich Ihnen das vorher, nicht nachher. Besucher kommen
              über Google, bezahlte Anzeigen und Empfehlungen. Deshalb baue ich jeden Shop
              conversion-optimiert und sage Ihnen offen, welches Budget Sie für Besucherquellen
              wie Google Ads oder Microsoft Ads einplanen sollten. Manchmal lautet meine
              Empfehlung: kleiner starten und den Rest in Besucher stecken. Das verkauft mehr
              als der perfekte Shop, den keiner kennt.
            </p>
          </div>
        </section>

        <CaseStrip
          slugs={["snydergolf", "maskabel", "deinlunu"]}
          title="Drei Projekte, drei Aufgaben"
          intro="Shopaufbau und Migration, Konfigurator- und Theme-Arbeit, Conversion-Arbeit an Produktseite und Kaufwegen."
        />

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-[1fr_1.1fr]">
            <h2 className="text-3xl font-bold leading-tight md:text-4xl">
              Warum Freelancer statt Agentur
            </h2>
            <ul className="grid gap-3">
              {reasons.map((r) => (
                <li key={r} className="flex items-start gap-3 text-muted-foreground">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>


        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Häufige Fragen</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Weitere Antworten in der{" "}
              <Link to="/faq" className="text-primary underline underline-offset-4">
                FAQ
              </Link>{" "}
              und ein Überblick unter{" "}
              <Link to="/leistungen" className="text-primary underline underline-offset-4">
                Leistungen
              </Link>
              .
            </p>
          </div>
        </section>

        <section id="kontakt" className="py-20 pb-28 lg:pb-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Schreiben Sie mir, <span className="text-signal">was ansteht</span>.
              </h2>
              <p className="mt-5 text-muted-foreground">
                Sie bekommen innerhalb von 48 Stunden eine kurze Einschätzung zu Ihrem
                Vorhaben, den empfohlenen nächsten Schritt und, soweit anhand Ihrer Angaben
                möglich, einen groben Kostenrahmen. Kostenlos und unverbindlich.
              </p>
            </div>
            <LeadForm
              source="lp-freelancer"
              compact={false}
              showServices
              cta="Kostenlose Einschätzung erhalten"
            />
          </div>
        </section>
      </main>
      <StickyCta targetId="anfrage" />
      <SiteFooter />
    </div>
  );
}
